// Serviço unificado de tokens que integra APIs e RPC
import { supabase } from '../config/supabase';
import type {
  DebitTokensRequest,
  DebitTokensResponse,
  CreditTokensRequest,
  CreditTokensResponse,
  BalanceResponse,
  TransactionsResponse
} from '../api/tokens';

export interface TokenServiceConfig {
  useAPI?: boolean; // Se true, usa APIs REST; se false, usa RPC direto
  enableAuditLog?: boolean; // Se true, habilita logs de auditoria
  enableDuplicationCheck?: boolean; // Se true, verifica duplicação de transações
}

export interface TokenAuditLog {
  id?: string;
  user_id: string;
  action: 'debit' | 'credit' | 'balance_check' | 'transaction_query';
  amount?: number;
  description?: string;
  metadata?: any;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;
  success: boolean;
  error_message?: string;
}

export interface TokenTransactionValidation {
  isValid: boolean;
  isDuplicate: boolean;
  existingTransactionId?: string;
  reason?: string;
}

class UnifiedTokenService {
  private config: TokenServiceConfig;

  constructor(config: TokenServiceConfig = { 
    useAPI: false, 
    enableAuditLog: false, 
    enableDuplicationCheck: true 
  }) {
    this.config = config;
  }

  /**
   * Log de auditoria para transações de tokens
   */
  private async logAudit(
    userId: string,
    action: TokenAuditLog['action'],
    success: boolean,
    amount?: number,
    description?: string,
    metadata?: any,
    errorMessage?: string
  ): Promise<void> {
    if (!this.config.enableAuditLog) return;

    try {
      const auditLog: Omit<TokenAuditLog, 'id'> = {
        user_id: userId,
        action,
        amount,
        description,
        metadata: {
          ...metadata,
          service_version: '2.0',
          timestamp_ms: Date.now()
        },
        timestamp: new Date().toISOString(),
        success,
        error_message: errorMessage
      };

      const { error } = await supabase
        .from('token_audit_logs')
        .insert([auditLog]);

      if (error) {
        console.error('Erro ao salvar log de auditoria:', error);
        // Não falhar a operação principal por causa do log
      }
    } catch (error) {
      console.error('Erro ao salvar log de auditoria:', error);
    }
  }

  /**
   * Verificar duplicação de transações
   */
  private async checkDuplication(
    userId: string,
    amount: number,
    description: string,
    metadata?: any
  ): Promise<TokenTransactionValidation> {
    if (!this.config.enableDuplicationCheck) {
      return { isValid: true, isDuplicate: false };
    }

    try {
      // Verificar transações similares nos últimos 5 minutos
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      
      const { data: similarTransactions, error } = await supabase
        .from('token_ledger')
        .select('id, metadata')
        .eq('user_uid', userId)
        .eq('amount', amount)
        .eq('reason', description)
        .gte('created_at', fiveMinutesAgo)
        .limit(5);

      if (error) {
        console.error('Erro ao verificar duplicação:', error);
        return { isValid: true, isDuplicate: false };
      }

      // Verificar se há transações com metadata similar
      if (similarTransactions && similarTransactions.length > 0 && metadata) {
        for (const transaction of similarTransactions) {
          const existingMetadata = transaction.metadata || {};
          
          // Verificar chaves específicas que indicam duplicação
          const duplicateKeys = ['payment_id', 'content_id', 'referral_code', 'purchase_id'];
          
          for (const key of duplicateKeys) {
            if (metadata[key] && existingMetadata[key] === metadata[key]) {
              return {
                isValid: false,
                isDuplicate: true,
                existingTransactionId: transaction.id,
                reason: `Transação duplicada detectada pela chave: ${key}`
              };
            }
          }
        }
      }

      return { isValid: true, isDuplicate: false };
    } catch (error) {
      console.error('Erro ao verificar duplicação:', error);
      return { isValid: true, isDuplicate: false };
    }
  }

  /**
   * Verifica se o usuário tem saldo suficiente (Helper)
   */
  async checkBalance(userId: string, amount: number): Promise<boolean> {
    try {
      const balance = await this.getBalance(userId);
      return balance >= amount;
    } catch (error) {
      console.error('Erro ao verificar saldo:', error);
      return false;
    }
  }

  /**
   * Consulta o saldo de tokens do usuário com log de auditoria
   */
  async getBalance(userUid: string): Promise<number> {
    const startTime = Date.now();
    let balance = 0;
    let success = false;
    let errorMessage: string | undefined;

    try {
      if (this.config.useAPI) {
        // Usar API REST
        const response = await fetch(`/api/tokens/balance?user_uid=${userUid}`);
        const data: BalanceResponse = await response.json();
        
        if (!data.success) {
          throw new Error(data.error || 'Erro ao consultar saldo');
        }
        
        balance = data.balance;
      } else {
        // Usar consulta direta na tabela users (padrão)
        let { data, error } = await supabase
          .from('users')
          .select('token_balance')
          .eq('uid', userUid)
          .single();

        if (error) {
          throw error;
        }

        balance = data?.token_balance || 0;
      }

      success = true;
      return balance;
    } catch (error: any) {
      errorMessage = error.message || 'Erro ao consultar saldo';
      console.error('Erro ao consultar saldo de tokens:', error);
      throw new Error(errorMessage);
    } finally {
      // Log de auditoria
      await this.logAudit(
        userUid,
        'balance_check',
        success,
        undefined,
        'Consulta de saldo',
        {
          processing_time_ms: Date.now() - startTime,
          balance_result: success ? balance : undefined
        },
        errorMessage
      );
    }
  }

  /**
   * Debita tokens do usuário com validações e logs
   */
  async debitTokens(
    userUid: string, 
    amount: number, 
    description: string, 
    metadata?: any
  ): Promise<DebitTokensResponse> {
    const startTime = Date.now();
    let result: DebitTokensResponse;
    let success = false;
    let errorMessage: string | undefined;

    try {
      // Validar entrada
      if (amount <= 0) {
        throw new Error('Quantidade deve ser maior que zero');
      }

      // Verificar duplicação
      const validation = await this.checkDuplication(userUid, -amount, description, metadata);
      if (!validation.isValid) {
        throw new Error(validation.reason || 'Transação duplicada detectada');
      }

      if (this.config.useAPI) {
        // Usar API REST
        const request: DebitTokensRequest = {
          user_uid: userUid,
          amount,
          description,
          metadata: {
            ...metadata,
            validation_passed: true,
            service_timestamp: new Date().toISOString()
          }
        };

        const response = await fetch('/api/tokens/debit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(request)
        });

        result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Erro ao debitar tokens');
        }
      } else {
        // Usar RPC direto (padrão) - Atualizado para usar debit_user_tokens conforme solicitado
        const { data, error } = await supabase.rpc('debit_user_tokens', {
          p_user_uid: userUid,
          p_amount: Math.abs(amount), // Valor positivo para debitar
          p_reason: description,
          p_metadata: {
            ...metadata,
            validation_passed: true,
            service_timestamp: new Date().toISOString(),
            processing_time_ms: Date.now() - startTime
          }
        });

        if (error) {
          throw error;
        }

        // Buscar novo saldo para manter compatibilidade de retorno
        let newBalance = 0;
        try {
            const { data: userData } = await supabase.from('users').select('token_balance').eq('uid', userUid).single();
            newBalance = userData?.token_balance || 0;
        } catch { }

        result = {
          success: !!data,
          previous_balance: newBalance + Math.abs(amount), // Estimado
          new_balance: newBalance,
          amount: -Math.abs(amount),
          transaction_type: 'debit',
          ledger_id: Date.now() // ID temporário pois o RPC não retorna ID do ledger
        };
      }

      success = true;
      return result;
    } catch (error: any) {
      errorMessage = error.message || 'Erro ao debitar tokens';
      console.error('Erro ao debitar tokens:', error);
      throw new Error(errorMessage);
    } finally {
      // Log de auditoria
      await this.logAudit(
        userUid,
        'debit',
        success,
        amount,
        description,
        {
          ...metadata,
          processing_time_ms: Date.now() - startTime,
          result_ledger_id: success ? result?.ledger_id : undefined
        },
        errorMessage
      );
    }
  }

  /**
   * Credita tokens ao usuário com validações e logs
   */
  async creditTokens(
    userUid: string, 
    amount: number, 
    description: string, 
    metadata?: any
  ): Promise<CreditTokensResponse> {
    const startTime = Date.now();
    let result: CreditTokensResponse;
    let success = false;
    let errorMessage: string | undefined;

    try {
      // Validar entrada
      if (amount <= 0) {
        throw new Error('Quantidade deve ser maior que zero');
      }

      // Verificar duplicação
      const validation = await this.checkDuplication(userUid, amount, description, metadata);
      if (!validation.isValid) {
        throw new Error(validation.reason || 'Transação duplicada detectada');
      }

      if (this.config.useAPI) {
        // Usar API REST
        const request: CreditTokensRequest = {
          user_uid: userUid,
          amount,
          description,
          metadata: {
            ...metadata,
            validation_passed: true,
            service_timestamp: new Date().toISOString()
          }
        };

        const response = await fetch('/api/tokens/credit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(request)
        });

        result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Erro ao creditar tokens');
        }
      } else {
        // Tentar RPC direto primeiro
        const { data, error } = await supabase.rpc('credit_user_tokens', {
          p_user_uid: userUid,
          p_amount: Math.abs(amount),
          p_reason: description,
          p_metadata: {
            ...metadata,
            validation_passed: true,
            service_timestamp: new Date().toISOString(),
            processing_time_ms: Date.now() - startTime
          }
        });

        if (error) {
          // Fallback para inserção manual se RPC não existir (PGRST202)
          if (error.code === 'PGRST202' || error.message?.includes('Could not find the function')) {
             console.warn('RPC credit_user_tokens não encontrada, usando fallback manual.');
             
             // 1. Buscar Saldo Atual (Necessário para calcular balance_after)
             const { data: user, error: fetchError } = await supabase.from('users').select('token_balance').eq('uid', userUid).single();
             if (fetchError) throw fetchError;
             
             const currentBalance = user?.token_balance || 0;
             const newBalance = currentBalance + Math.abs(amount);

             // 2. Inserir no Ledger
             const { error: ledgerError } = await supabase.from('token_ledger').insert({
                 user_uid: userUid,
                 amount: Math.abs(amount),
                 reason: description,
                 metadata: metadata,
                 transaction_type: 'credit',
                 balance_after: newBalance // Adicionado para satisfazer constraint NOT NULL
             });
             if (ledgerError) throw ledgerError;

             // 3. Atualizar Saldo do Usuário
             const { error: updateError } = await supabase.from('users').update({ token_balance: newBalance }).eq('uid', userUid);
             if (updateError) throw updateError;

             // Sucesso manual
          } else {
             throw error;
          }
        }

        // Buscar novo saldo para manter compatibilidade de retorno
        let newBalance = 0;
        try {
            const { data: userData } = await supabase.from('users').select('token_balance').eq('uid', userUid).single();
            newBalance = userData?.token_balance || 0;
        } catch { }

        result = {
          success: !!data,
          previous_balance: newBalance - Math.abs(amount), // Estimado
          new_balance: newBalance,
          amount: Math.abs(amount),
          transaction_type: 'credit',
          ledger_id: Date.now() // ID temporário
        };
      }

      success = true;
      return result;
    } catch (error: any) {
      errorMessage = error.message || 'Erro ao creditar tokens';
      console.error('Erro ao creditar tokens:', error);
      throw new Error(errorMessage);
    } finally {
      // Log de auditoria
      await this.logAudit(
        userUid,
        'credit',
        success,
        amount,
        description,
        {
          ...metadata,
          processing_time_ms: Date.now() - startTime,
          result_ledger_id: success ? result?.ledger_id : undefined
        },
        errorMessage
      );
    }
  }

  /**
   * Consulta o extrato de transações
   */
  async getTransactions(
    userUid: string, 
    limit: number = 20, 
    offset: number = 0
  ): Promise<TransactionsResponse> {
    try {
      if (this.config.useAPI) {
        // Usar API REST
        const response = await fetch(
          `/api/tokens/transactions?user_uid=${userUid}&limit=${limit}&offset=${offset}`
        );
        const data: TransactionsResponse = await response.json();
        
        if (!data.success) {
          throw new Error(data.error || 'Erro ao consultar extrato');
        }
        
        return data;
      } else {
        // Usar consulta direta (padrão)
        const { data: transactions, error, count } = await supabase
          .from('token_ledger')
          .select('*', { count: 'exact' })
          .eq('user_uid', userUid)
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1);

        if (error) {
          throw error;
        }

        return {
          success: true,
          transactions: transactions || [],
          total_count: count || 0
        };
      }
    } catch (error: any) {
      console.error('Erro ao consultar extrato:', error);
      throw new Error(error.message || 'Erro ao consultar extrato');
    }
  }

  /**
   * Operações de conveniência
   */
  
  // Desbloquear conteúdo premium
  async unlockContent(userUid: string, contentId: string, cost: number): Promise<DebitTokensResponse> {
    return this.debitTokens(
      userUid, 
      cost, 
      `Desbloqueio de conteúdo premium`, 
      { content_id: contentId, action: 'unlock_content' }
    );
  }

  // Bônus de boas-vindas
  async giveWelcomeBonus(userUid: string): Promise<CreditTokensResponse> {
    return this.creditTokens(
      userUid, 
      20, 
      'Bônus de boas-vindas', 
      { bonus_type: 'welcome', action: 'welcome_bonus' }
    );
  }

  // Bônus de indicação
  async giveReferralBonus(userUid: string, referralCode?: string): Promise<CreditTokensResponse> {
    return this.creditTokens(
      userUid, 
      10, 
      'Bônus de indicação', 
      { bonus_type: 'referral', referral_code: referralCode, action: 'referral_bonus' }
    );
  }

  // Crédito por compra (webhook MP)
  async creditPurchase(
    userUid: string, 
    tokenAmount: number, 
    paymentId: string, 
    reaisAmount: number
  ): Promise<CreditTokensResponse> {
    return this.creditTokens(
      userUid, 
      tokenAmount, 
      `Compra de tokens - Pagamento ${paymentId}`, 
      { 
        payment_id: paymentId, 
        reais_amount: reaisAmount, 
        action: 'token_purchase' 
      }
    );
  }
}

// Instância padrão (usa RPC direto)
export const tokenService = new UnifiedTokenService({ useAPI: false });

// Instância para APIs REST (para testes ou casos específicos)
export const tokenAPIService = new UnifiedTokenService({ useAPI: true });

// Exportar a classe para casos customizados
export { UnifiedTokenService };

// Funções de conveniência (mantém compatibilidade com código existente)
export const getUserTokenBalance = (userUid: string) => tokenService.getBalance(userUid);
export const spendUserTokens = (userUid: string, amount: number, description: string, metadata?: any) => 
  tokenService.debitTokens(userUid, amount, description, metadata);
export const addTokensToUser = (userUid: string, amount: number, description: string, metadata?: any) => 
  tokenService.creditTokens(userUid, amount, description, metadata);
export const getUserTokenTransactions = (userUid: string, limit?: number, offset?: number) => 
  tokenService.getTransactions(userUid, limit, offset);